import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Menu, Heart, BookOpen } from "lucide-react";
import portalApi, { portalStorage, formatPortalError } from "./portalApi";
import { PortalHeader, useLearner } from "./PortalDashboard";
import { previewStore, PREVIEW_LEARNER } from "./previewStore";
import PreviewBanner from "./PreviewBanner";
import LearnerLiveClasses from "./LearnerLiveClasses";
import api from "../../lib/api";
import usePageMeta from "../../lib/usePageMeta";

const COURSE_CODE = "HOSP-001";
const MODEL = ["See", "Listen", "Explore", "Support", "Honor"];

export default function PortalHospLearn() {
  usePageMeta({ title: "HOSP-001 — See the Person First | CCCHAPS", description: "" });
  const inPreview = previewStore.isActive() && previewStore.getCourseCode() === "HOSP-001";
  const learner = useLearner();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [pos, setPos] = useState(0);
  const [nav_open, setNavOpen] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  const buildPreviewBundle = (course) => {
    const s = previewStore.getState();
    const module_ids = (course.modules || []).map(m => m.id);
    const allDone = module_ids.length && module_ids.every(id => (s.progress?.section_completions || {})[id]);
    return {
      course,
      enrollment: {
        id: "preview",
        learner_id: "preview",
        course_id: course.id,
        course_code: course.code,
        status: allDone ? "completed" : "in_progress",
        enrolled_at: s.started_at,
        completed_at: allDone ? new Date().toISOString() : null,
      },
      progress: s.progress,
    };
  };

  const load = async () => {
    try {
      if (inPreview) {
        const r = await api.get("/admin/portal/preview/course", { params: { code: COURSE_CODE } });
        setData(buildPreviewBundle(r.data.course));
        return;
      }
      const r = await portalApi.get(`/portal/courses/${COURSE_CODE}`);
      setData(r.data);
    } catch (err) {
      if (inPreview) { toast.error("Preview mode requires admin sign-in."); previewStore.exit(); nav("/care-portal/admin/academy-portal"); return; }
      toast.error(formatPortalError(err));
    }
  };

  useEffect(() => { if (learner) load(); /* eslint-disable-next-line */ }, [learner]);

  const enroll = async () => {
    if (inPreview) return; // no-op in preview
    setEnrolling(true);
    try {
      await portalApi.post("/portal/enroll", { course_code: COURSE_CODE });
      await load();
      toast.success("You're enrolled in HOSP-001.");
    } catch (err) { toast.error(formatPortalError(err)); } finally { setEnrolling(false); }
  };

  const markComplete = async (sectionId) => {
    if (inPreview) {
      previewStore.markSectionComplete(sectionId);
      await load();
      return;
    }
    try {
      await portalApi.post("/portal/progress/section-complete", { course_code: COURSE_CODE, section_id: sectionId });
      await load();
    } catch (err) { toast.error(formatPortalError(err)); }
  };

  if (!learner || !data) return (
    <div className="min-h-screen bg-[#FDFCF6]">
      <PortalHeader learner={learner}/>
      <div className="p-12 text-center text-slate-500">Loading…</div>
    </div>
  );

  const course = data.course;
  const enrollment = data.enrollment;
  const completions = data.progress?.section_completions || {};
  const lessons = course.modules || [];
  const isEnrolled = !!enrollment;
  const isCompleted = enrollment?.status === "completed";
  const doneCount = lessons.filter(l => completions[l.id]).length;
  const pct = lessons.length ? Math.round(doneCount * 100 / lessons.length) : 0;

  // Steps: [overview, ...lessons, completion]
  const totalSteps = lessons.length + 2;
  const current = pos;

  const goNext = () => setPos(p => Math.min(p + 1, totalSteps - 1));
  const goPrev = () => setPos(p => Math.max(p - 1, 0));

  return (
    <div className="bg-[#FDFCF6] min-h-screen" data-testid="portal-hosp-learn">
      {inPreview && (
        <PreviewBanner
          course={data?.course}
          resetTo="/academy/portal/hosp-001/learn"
          onSyncPreview={() => { setPos(0); load(); }}
        />
      )}
      <PortalHeader learner={learner}/>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-10 grid lg:grid-cols-[260px_1fr] gap-6">
        {/* Sidebar */}
        <aside className={`${nav_open ? 'block' : 'hidden'} lg:block`} data-testid="hosp-learn-sidebar">
          <div className="bg-white border border-slate-200 rounded-2xl p-4 sticky top-24">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold">HOSP-001</p>
            <h3 className="mt-1 font-heading text-base text-slate-900 leading-tight">See the Person First</h3>
            <p className="mt-1 text-[11px] text-slate-500">Track Three Preview</p>
            <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
              <span>{doneCount}/{lessons.length} lessons</span><span>{pct}%</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1"><div className="h-full bg-[#4A6FA5]" style={{width: `${pct}%`}}/></div>
            <ul className="mt-4 space-y-1 text-sm">
              <li>
                <button onClick={() => { setPos(0); setNavOpen(false); }} className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left ${current === 0 ? 'bg-[#F0F4F8] text-[#3B597B] font-medium' : 'text-slate-700 hover:bg-slate-50'}`}>
                  <Circle className="w-3.5 h-3.5"/> <span>Overview</span>
                </button>
              </li>
              {lessons.map((l, i) => {
                const done = !!completions[l.id];
                return (
                  <li key={l.id}>
                    <button onClick={() => { setPos(i + 1); setNavOpen(false); }} className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left ${current === i + 1 ? 'bg-[#F0F4F8] text-[#3B597B] font-medium' : 'text-slate-700 hover:bg-slate-50'}`} data-testid={`hosp-nav-${l.id}`}>
                      {done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> : <Circle className="w-3.5 h-3.5 text-slate-400"/>}
                      <span className="truncate">Lesson {l.order}: {l.title}</span>
                    </button>
                  </li>
                );
              })}
              <li>
                <button onClick={() => { setPos(totalSteps - 1); setNavOpen(false); }} disabled={!isCompleted} className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left ${current === totalSteps - 1 ? 'bg-[#F0F4F8] text-[#3B597B] font-medium' : 'text-slate-700 hover:bg-slate-50'} disabled:opacity-40 disabled:cursor-not-allowed`}>
                  <Heart className="w-3.5 h-3.5"/> <span>Completion</span>
                </button>
              </li>
            </ul>
          </div>
        </aside>

        <main className="min-w-0">
          <div className="lg:hidden mb-4">
            <button onClick={() => setNavOpen(v => !v)} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700"><Menu className="w-4 h-4"/> {nav_open ? "Close" : "Lessons"}</button>
          </div>

          {/* Overview */}
          {current === 0 && (
            <>
              <OverviewView course={course} enrollment={enrollment} onEnrollClick={enroll} enrolling={enrolling} onStart={() => setPos(1)} isCompleted={isCompleted} pct={pct} />
              {isEnrolled && <LearnerLiveClasses courseCode="HOSP-001" title="From the Book to the Bedside — Live" testId="hosp-live-classes" />}
            </>
          )}

          {/* Lessons */}
          {current >= 1 && current <= lessons.length && (
            <LessonView
              lesson={lessons[current - 1]}
              isEnrolled={isEnrolled}
              done={!!completions[lessons[current - 1].id]}
              onMarkDone={async () => { await markComplete(lessons[current - 1].id); }}
              onNext={goNext}
              onPrev={goPrev}
              isLast={current === lessons.length}
              onEnrollClick={enroll}
              enrolling={enrolling}
            />
          )}

          {/* Completion */}
          {current === totalSteps - 1 && (
            <>
              <CompletionView />
              {isEnrolled && <LearnerLiveClasses courseCode="HOSP-001" title="From the Book to the Bedside — Live" testId="hosp-live-classes-completion" />}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function OverviewView({ course, enrollment, onEnrollClick, enrolling, onStart, isCompleted, pct }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8" data-testid="hosp-overview">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-800 mb-4">Free Track Three Preview</span>
      <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">HOSP-001</p>
      <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">See the Person First</h1>
      <p className="mt-2 text-slate-600 italic">An Introduction to Patient-Centered Hospice Chaplaincy</p>

      <dl className="mt-6 grid sm:grid-cols-2 gap-2 text-sm">
        {[
          ["Course", "HOSP-001"],
          ["Length", "Approx. 35–45 minutes"],
          ["Level", "Introductory"],
          ["Format", "Online / Self-Paced"],
          ["Cost", "Free"],
          ["Academy Pathway", "Track Three Preview"],
        ].map(([k,v]) => (
          <div key={k} className="flex justify-between gap-4 rounded-lg bg-[#F8FAFC] px-3 py-2 border border-slate-100">
            <dt className="text-slate-500">{k}</dt><dd className="font-medium text-slate-900 text-right">{v}</dd>
          </div>
        ))}
      </dl>

      <p className="mt-6 text-slate-700 leading-relaxed">
        HOSP-001 introduces the patient-centered philosophy behind Track Three: Hospice Chaplaincy — Practicing From Diagnosis to Goodbye. Participants learn to begin hospice chaplaincy by seeing the person, hearing the patient's voice, and understanding the patient's experience before deciding how to respond.
      </p>

      <div className="mt-6 rounded-xl bg-[#F0F4F8] p-4">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">Teaching model</p>
        <p className="mt-1.5 font-heading text-sm text-slate-900 font-medium">{MODEL.join("  →  ")}</p>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        {!enrollment ? (
          <button onClick={onEnrollClick} disabled={enrolling} data-testid="hosp-enroll-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] disabled:opacity-60">
            {enrolling ? "Enrolling…" : "Start Course"} <ArrowRight className="w-4 h-4"/>
          </button>
        ) : (
          <button onClick={onStart} data-testid="hosp-start-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">
            {isCompleted ? "Review Course" : (pct > 0 ? "Continue Course" : "Start Course")} <ArrowRight className="w-4 h-4"/>
          </button>
        )}
      </div>

      <p className="mt-6 text-[11px] text-slate-500 leading-relaxed">
        HOSP-001 is introductory educational training. Completion does not confer professional licensure, clinical certification, accredited continuing-education credit, hospice certification, or authority to provide medical, mental-health, or clinical treatment.
      </p>
      {enrollment && <HospLiveOptional />}
    </div>
  );
}

function HospLiveOptional() {
  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-[#FBFCFE] p-5" data-testid="hosp-live-optional">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">Optional Live Discussion</p>
      <h3 className="mt-1 font-heading text-xl text-slate-900">From the Book to the Bedside — Live</h3>
      <p className="mt-2 text-sm text-slate-700 leading-relaxed">
        An opportunity to participate in interactive online discussions with Chaplain Derrick Hall — hospice case scenarios, patient-perspective exercises, Q&amp;A, and practical application of See → Listen → Explore → Support → Honor.
      </p>
      <p className="mt-2 text-xs text-slate-500 leading-relaxed">
        Live sessions are optional. HOSP-001 remains fully self-paced and can be completed without attending a live session.
      </p>
    </div>
  );
}

function LessonView({ lesson, isEnrolled, done, onMarkDone, onNext, onPrev, isLast, onEnrollClick, enrolling }) {
  const [answered, setAnswered] = useState(null);   // {key, correct, feedback} for quick-check
  return (
    <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8" data-testid={`hosp-lesson-${lesson.id}`}>
      <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold">Lesson {lesson.order} · ~{lesson.estimated_minutes} min</p>
      <h1 className="mt-2 font-heading text-3xl tracking-tight text-slate-900 leading-tight">{lesson.title}</h1>
      <p className="mt-4 text-slate-700 leading-relaxed">{lesson.intro}</p>

      <div className="mt-8 space-y-6">
        {lesson.sections.map((s, i) => (
          <LessonSection key={i} section={s} answered={answered} setAnswered={setAnswered} />
        ))}
      </div>

      <div className="mt-10 flex items-center justify-between gap-3 border-t border-slate-100 pt-6">
        <button onClick={onPrev} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:border-[#4A6FA5]">
          <ArrowLeft className="w-4 h-4"/> Previous
        </button>
        <div className="flex items-center gap-3">
          {!isEnrolled ? (
            <button onClick={onEnrollClick} disabled={enrolling} data-testid="hosp-lesson-enroll" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] disabled:opacity-60">
              {enrolling ? "Enrolling…" : "Enroll to Track Progress"}
            </button>
          ) : (
            <button onClick={async () => { if (!done) await onMarkDone(); onNext(); }} data-testid={`hosp-lesson-next-${lesson.id}`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]">
              {done ? (isLast ? "Continue" : "Next Lesson") : (isLast ? "Mark Complete & Continue" : "Mark Complete & Continue")} <ArrowRight className="w-4 h-4"/>
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function LessonSection({ section, answered, setAnswered }) {
  if (section.type === "text") return (
    <div>
      {section.heading && <h3 className="font-heading text-lg text-slate-900 mb-2">{section.heading}</h3>}
      <p className="text-slate-700 leading-relaxed">{section.body}</p>
    </div>
  );
  if (section.type === "list") return (
    <div>
      {section.heading && <h3 className="font-heading text-lg text-slate-900 mb-2">{section.heading}</h3>}
      <ul className="space-y-1.5">
        {section.items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-slate-700 leading-relaxed"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#4A6FA5] shrink-0"/><span>{it}</span></li>
        ))}
      </ul>
    </div>
  );
  if (section.type === "highlight") return (
    <div className="rounded-xl border-l-4 border-[#4A6FA5] bg-[#F0F4F8] p-4">
      <p className="font-heading text-slate-900">{section.body}</p>
    </div>
  );
  if (section.type === "reflection") return (
    <div className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-4" data-testid="hosp-reflection">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold mb-1.5">Private reflection</p>
      <p className="text-sm text-slate-700 leading-relaxed">{section.prompt}</p>
      <textarea rows={3} placeholder="For your own reflection. Not stored." className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] resize-none"/>
    </div>
  );
  if (section.type === "quick-check") {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hosp-quick-check">
        <p className="font-heading text-slate-900 mb-3">{section.prompt}</p>
        <div className="space-y-2">
          {section.options.map(opt => {
            const isPicked = answered?.key === opt.key;
            const showFeedback = !!answered && isPicked;
            return (
              <button key={opt.key} onClick={() => setAnswered({ key: opt.key, correct: !!opt.correct, feedback: opt.feedback })} data-testid={`hosp-quick-check-${opt.key}`}
                      className={`w-full text-left rounded-lg border px-3 py-2.5 text-sm transition-colors ${isPicked ? (opt.correct ? "border-emerald-400 bg-emerald-50 text-emerald-900" : "border-amber-400 bg-amber-50 text-amber-900") : "border-slate-200 bg-white text-slate-800 hover:border-[#4A6FA5]"}`}>
                <span className="font-medium mr-2">{opt.key.toUpperCase()}.</span>{opt.label}
                {showFeedback && <p className="mt-2 text-xs leading-relaxed opacity-90">{opt.feedback}</p>}
              </button>
            );
          })}
        </div>
        {answered && (
          <p className={`mt-3 text-xs font-medium ${answered.correct ? "text-emerald-700" : "text-slate-500"}`}>{answered.correct ? "That's the patient-centered posture." : "Consider trying another option or reviewing the teaching."}</p>
        )}
      </div>
    );
  }
  return null;
}

function CompletionView() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10 text-center" data-testid="hosp-completion">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 mb-5">
        <CheckCircle2 className="w-7 h-7" strokeWidth={2}/>
      </div>
      <p className="text-xs uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">HOSP-001 Complete</p>
      <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">See the Person. Hear the Voice. Honor the Journey.</h1>
      <p className="mt-4 text-slate-700 leading-relaxed max-w-2xl mx-auto">
        You have completed HOSP-001 — See the Person First: An Introduction to Patient-Centered Hospice Chaplaincy.
      </p>
      <p className="mt-4 text-slate-700 leading-relaxed max-w-2xl mx-auto">
        The central principle is simple:<br/>
        <em>Before deciding what the chaplain should do, first consider what the patient may be experiencing.</em>
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Link to="/academy/hospice-chaplaincy" data-testid="hosp-completion-track3" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]">
          Explore Track Three: Hospice Chaplaincy <ArrowRight className="w-4 h-4"/>
        </Link>
        <Link to="/books/from-diagnosis-to-goodbye" data-testid="hosp-completion-book" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-800 text-sm font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]">
          <BookOpen className="w-4 h-4"/> Explore From Diagnosis to Goodbye
        </Link>
      </div>
      <p className="mt-8 text-[11px] text-slate-500 leading-relaxed max-w-3xl mx-auto">
        HOSP-001 is introductory educational training. Completion does not confer professional licensure, clinical certification, accredited continuing-education credit, hospice certification, or authority to provide medical, mental-health, or clinical treatment.
      </p>
    </div>
  );
}
